/**
 * Bone lengths for the 1.8 m character and the two-bone IK used for hands
 * on bars and feet on the ground. All math is in world meters with y up.
 */

export const BONES = {
  torso: 0.55,
  neck: 0.05,
  headLen: 0.22,
  headRadius: 0.115,
  upperArm: 0.3,
  forearm: 0.28,
  thigh: 0.45,
  shin: 0.45,
  /** Capsule radii for drawing. */
  torsoRadius: 0.13,
  limbRadius: 0.055,
  shinRadius: 0.05,
} as const;

export interface IkResult {
  /** Angle of the first bone from straight down (radians, positive toward +x), world frame. */
  a: number;
  /** Flexion at the middle joint, radians, always >= 0. */
  flex: number;
}

/**
 * Two-bone IK from a root to a target with bone lengths l1 and l2, in the
 * world frame. `bendSign` is +1 when the middle joint should sit on the +x
 * side of the root-to-target line and -1 for the -x side. Beyond `soft`
 * (a fraction of the full reach) the effective distance saturates smoothly,
 * so the joint angle never has the infinite slope a straight limb would give;
 * the caller may stretch the second bone to keep the tip on the target.
 * The second bone's angle is `a - bendSign * flex`.
 */
export function twoBoneIk(
  rootX: number,
  rootY: number,
  targetX: number,
  targetY: number,
  l1: number,
  l2: number,
  bendSign: number,
  out: IkResult,
  soft = 1,
): void {
  const dx = targetX - rootX;
  const dy = targetY - rootY;
  let d = Math.sqrt(dx * dx + dy * dy);
  const full = l1 + l2;
  const maxReach = full * 0.998;
  const minReach = Math.abs(l1 - l2) + 1e-4;
  if (soft < 1) {
    const start = full * soft;
    if (d > start) {
      const span = maxReach - start;
      d = start + span * (1 - Math.exp(-(d - start) / span));
    }
  }
  if (d > maxReach) d = maxReach;
  if (d < minReach) d = minReach;
  const lineAngle = d > 1e-9 ? Math.atan2(dx, -dy) : 0;
  let cosA = (l1 * l1 + d * d - l2 * l2) / (2 * l1 * d);
  if (cosA > 1) cosA = 1;
  if (cosA < -1) cosA = -1;
  const alpha = Math.acos(cosA);
  let cosB = (l1 * l1 + l2 * l2 - d * d) / (2 * l1 * l2);
  if (cosB > 1) cosB = 1;
  if (cosB < -1) cosB = -1;
  const inner = Math.acos(cosB);
  out.a = lineAngle + bendSign * alpha;
  out.flex = Math.PI - inner;
}

/** Shortest signed difference between two angles, radians. */
export function angleDelta(from: number, to: number): number {
  let d = to - from;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d < -Math.PI) d += 2 * Math.PI;
  return d;
}
